import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./PersonalItem.module.css";
import { formattedPhone } from "components/shared/formattedPhone";
import verify from "./Verified_Icon.svg";
import { Link } from "react-router-dom";
import { Button } from "../Button/Button";
import { useTranslation } from "react-i18next";
import environment from "../../environment";
import mobile from "mobileBridge";

import { AsYouType } from "libphonenumber-js";

/**
 * Interactive component, a button that takes a function as a parameter
 */
export function PersonalItem({
  verified,
  onVerify,
  editable,
  label,
  info,
  onSave,
  onToggleEdit,
  isEditing,
  error,
  isPhoneNumber,
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState(info);
  const [openModal, setOpenModal] = useState(false);
  const [isValid, setIsValid] = useState(false);

  const isMobile = mobile.isApp();

  useEffect(() => {
    setInput(info);
  }, [info]);

  const handleOnSave = () => {
    onSave(input);
    setIsValid(true);
  };

  const handleInputChange = (e) => {
    let inputValue = e.target.value;

    if (isPhoneNumber) {
      if (isMobile) {
        // Mobile: preserve cursor position
        const inputElem = e.target;
        const selectionStart = inputElem.selectionStart;
        const rawBefore = inputValue
          .slice(0, selectionStart)
          .replace(/\D/g, "");

        // Remove non-numeric characters and limit to 15 digits
        let phoneInput = inputValue.replace(/\D/g, "").slice(0, 15);

        // Format phone number
        const formatted = new AsYouType(environment.WEB_APP_COUNTRY).input(
          phoneInput
        );
        setInput(formatted);

        // Restore cursor position after formatting
        setTimeout(() => {
          if (inputElem) {
            let pos = 0,
              rawCount = 0;
            while (rawCount < rawBefore.length && pos < formatted.length) {
              if (/\d/.test(formatted[pos])) rawCount++;
              pos++;
            }
            inputElem.setSelectionRange(pos, pos);
          }
        }, 0);
      } else {
        // Desktop: just format and update
        let phoneInput = inputValue.replace(/\D/g, "").slice(0, 15);
        const formatted = new AsYouType(environment.WEB_APP_COUNTRY).input(
          phoneInput
        );
        setInput(formatted);
      }
    } else {
      setIsValid(!!inputValue);
      setInput(inputValue);
    }
  };

  const handleVerify = () => {
    setOpenModal(true);
  };

  const handleReset = () => {
    onToggleEdit();
    setInput(info);
  };

  return (
    <div className={styles.container}>
      {isEditing ? (
        <div className={styles.editContainer}>
          <div className={styles.inputContainer}>
            <input
              id={`${label}-input`}
              className={error ? styles.inputError : styles.input}
              type="text"
              value={input}
              onChange={handleInputChange}
              placeholder={label}
            />
          </div>
          <div className={styles.btnContainer}>
            <button className={styles.editBtn} onClick={handleReset}>
              {t("Cancel")}
            </button>
            <button
              className={classNames(styles.editBtn, styles.saveBtn)}
              onClick={handleOnSave}
            >
              {t("Save")}
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.editContainer}>
          <div className={styles.labelContainer}>
            {label}: <span className={styles.info}>{info}</span>
          </div>
          {editable && (
            <div className={styles.btnContainer}>
              {verified === true ? (
                <img
                  src={verify}
                  className={styles.image}
                  alt="Verified Icon"
                />
              ) : (
                info &&
                onVerify && (
                  <Link to="/verify">
                    <button
                      className={classNames(styles.editBtn, styles.verifyBtn)}
                    >
                      {t("Verify")}
                    </button>
                  </Link>
                )
              )}
              <button className={styles.editBtn} onClick={onToggleEdit}>
                {t("Edit")}
              </button>
            </div>
          )}
        </div>
      )}
      <div className={styles.errorContainer}>
        {isPhoneNumber && error && typeof error === "string" && (
          <span className={styles.error}>{error}</span>
        )}
      </div>
    </div>
  );
}

PersonalItem.propTypes = {
  /**
   * Shows verified badge
   */
  verified: PropTypes.bool,
  /**
   * Optional function if verify btn is present – called when verify is clicked
   */
  onVerify: PropTypes.bool,
  /**
   * Decides if item is editable
   */
  editable: PropTypes.bool,
  /**
   * Label shown on far left
   */
  label: PropTypes.string,
  /**
   * Info next to the label
   */
  info: PropTypes.string,
  /**
   * Function called when editing if save is clicked
   */
  onSave: PropTypes.func,
};
